import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../../../components/shared/toast';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { apiClient } from '../../../api/apiClient';
import { GET_SONG_ALBUMS_URL, REMOVE_SONG_ALBUM_URL } from '../../../api/song';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';

function AlbumsLabelsTab({ song, onSelectAlbum }) {
  const [albums, setAlbums] = useState([]);
  const [recordLabels, setRecordLabels] = useState([]);
  const [versionAlbums, setVersionAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Delete modal state
  const [pendingAlbumToRemove, setPendingAlbumToRemove] = useState(null);
  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);

  const fetchSongAlbumsAndLabels = useCallback(async () => {
    if (!song || !song.id) return;
    try {
      setIsLoading(true);
      const res = await apiClient(GET_SONG_ALBUMS_URL(song.id));
      if (res) {
        setAlbums(res.albums || []);
        setRecordLabels(res.recordLabels || []);
        setVersionAlbums(res.versionAlbums || []);
      }
    } catch (err) {
      console.error('Failed to fetch song albums and labels:', err);
    } finally {
      setIsLoading(false);
    }
  }, [song?.id]);

  useEffect(() => {
    fetchSongAlbumsAndLabels();
  }, [song?.id]);

  const handleInitiateRemoveAlbum = (alb) => {
    setPendingAlbumToRemove(alb);
    setShowConfirmRemoveModal(true);
  };

  const handleConfirmRemoveAlbum = async () => {
    if (!pendingAlbumToRemove || !song || !song.id) return;
    setShowConfirmRemoveModal(false);
    try {
      const res = await apiClient(REMOVE_SONG_ALBUM_URL(song.id, pendingAlbumToRemove.id), {
        method: 'DELETE'
      });
      if (res && res.success !== false) {
        toast.success('Album removed from song successfully');
        await fetchSongAlbumsAndLabels();
      }
    } catch (err) {
      console.error('Error removing album from song:', err);
      toast.error(err.message || 'Failed to remove album from song.');
    } finally {
      setPendingAlbumToRemove(null);
    }
  };

  const colorClasses = ['label-purple', 'label-blue', 'label-green', 'label-orange'];

  return (
    <div className="albums-labels-container" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      
      {/* 1. Record Labels Section */}
      <div className="albums-labels-section">
        <h3 className="section-title" style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
          Including in {recordLabels.length} {recordLabels.length === 1 ? 'Label' : 'Labels'}
        </h3>
        {isLoading ? (
          <Loader label="Fetching record labels, please wait." />
        ) : recordLabels.length === 0 ? (
          <EmptyState title="No Record Labels" subtitle="This song has no associated record labels yet." />
        ) : (
          <div className="labels-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
            {recordLabels.map((lbl, idx) => {
              const colorClass = colorClasses[idx % colorClasses.length];
              const img = lbl.image_url || lbl.imageUrl;
              const labelName = lbl.display_name || lbl.name || 'Record Label';
              const initial = labelName.charAt(0).toUpperCase();

              return (
                <div key={lbl.id || idx} className="label-badge-card">
                  <div className="label-card-thumb">
                    {img ? (
                      <img 
                        src={img} 
                        alt={labelName} 
                        className="label-card-img-thumb"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.label-icon-wrapper');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`label-icon-wrapper ${colorClass}`}
                      style={{ display: img ? 'none' : 'flex' }}
                    >
                      <span className="label-initial">{initial}</span>
                    </div>
                  </div>
                  <div className="label-card-info">
                    <span className="label-card-name" title={labelName}>{labelName}</span>
                    <span className="label-card-subtext">Record Label</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className="section-divider" style={{ margin: '0', border: 'none', borderTop: '1px solid #f1f5f9' }} />

      {/* 2. Direct Albums Section */}
      <div className="albums-labels-section">
        <div className="section-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="section-title" style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            {albums.length} {albums.length === 1 ? 'Album' : 'Albums'}
          </h3>
        </div>

        {isLoading ? (
          <Loader label="Fetching albums, please wait." />
        ) : albums.length === 0 ? (
          <EmptyState title="No Albums Found" subtitle="This song has no albums associated yet." />
        ) : (
          <div className="albums-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {albums.map((alb) => (
              <div 
                key={alb.id} 
                className="album-card-item"
                onClick={() => onSelectAlbum && onSelectAlbum(alb)}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                  <button
                    type="button"
                    className="album-dots-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInitiateRemoveAlbum(alb);
                    }}
                    title="Remove album relationship"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                </div>
                
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <div style={{ width: '84px', height: '84px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#f1f5f9', flexShrink: 0 }}>
                    {alb.coverUrl || alb.imageUrl || alb.image_url ? (
                      <img 
                        src={alb.coverUrl || alb.imageUrl || alb.image_url} 
                        alt={alb.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '24px' }}>🎵</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                    <h5 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {alb.name}
                    </h5>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{alb.type || 'Album Type'}</span>
                    <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      <span>📅 {alb.release_year || alb.releaseYear || alb.year || '—'}</span>
                      <span>🎵 {alb.tracks || `${alb.track_count || 0} Tracks`}</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '4px' }}>
                  <button 
                    type="button" 
                    className="action-btn-outlined" 
                    style={{ width: '100%', padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectAlbum && onSelectAlbum(alb);
                    }}
                  >
                    View Album
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className="section-divider" style={{ margin: '0', border: 'none', borderTop: '1px solid #f1f5f9' }} />

      {/* 3. Versions Albums Section */}
      <div className="albums-labels-section">
        <div className="section-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="section-title" style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            Versions albums
          </h3>
        </div>

        {isLoading ? (
          <Loader label="Fetching version albums, please wait." />
        ) : versionAlbums.length === 0 ? (
          <EmptyState title="No Record Found" subtitle="No version albums associated with version songs of this song." />
        ) : (
          <div className="versions-albums-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {versionAlbums.map((valb) => (
              <div 
                key={valb.id} 
                className="album-card-item version-album-card"
                onClick={() => onSelectAlbum && onSelectAlbum(valb)}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                  <button
                    type="button"
                    className="album-dots-btn"
                    onClick={(e) => e.stopPropagation()}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <div style={{ width: '84px', height: '84px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#f1f5f9', flexShrink: 0 }}>
                    {valb.coverUrl || valb.imageUrl || valb.image_url ? (
                      <img 
                        src={valb.coverUrl || valb.imageUrl || valb.image_url} 
                        alt={valb.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '24px' }}>🎵</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <h5 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {valb.name}
                      </h5>
                      {valb.type && (
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#6366f1', backgroundColor: '#e0e7ff', padding: '2px 8px', borderRadius: '12px' }}>
                          {valb.type}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Version Type</span>
                    <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      <span>📅 {valb.release_year || valb.releaseYear || valb.year || '—'}</span>
                      <span>🎵 {valb.tracks || `${valb.track_count || 0} Tracks`}</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button 
                    type="button" 
                    className="action-btn-outlined" 
                    style={{ padding: '8px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectAlbum && onSelectAlbum(valb);
                    }}
                  >
                    View Album
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal — Remove Album Relationship */}
      <ConfirmationModal
        isOpen={showConfirmRemoveModal}
        type="danger"
        title="Remove Album from Song"
        message={`Are you sure you want to remove the album "${pendingAlbumToRemove?.name}" from this song?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveAlbum}
        onCancel={() => {
          setShowConfirmRemoveModal(false);
          setPendingAlbumToRemove(null);
        }}
      />
    </div>
  );
}

export default AlbumsLabelsTab;
